import React, { useState, useEffect, useCallback } from 'react';
import { useContextoSesion } from '../../miscelaneo/ContextoSesion';
import instanciaAxios from '../../miscelaneo/instanciaAxios';
import { toast } from 'react-toastify';
import FuncionesGenerales from '../util/FuncionesGenerales';
import FuncionesProcesoNotasFactura from './Funciones/FuncionesProcesoNotasFactura';
import SpinnerCargando from '../util/SpinnerCargando';
import PlantillaModal from '../util/PlantillaModal';
import TablaNotasFactura from '../util/tablas/TablaNotasFactura';
import TablaFactura from '../util/tablas/TablaFactura';
import ModalCrearNotaFactura from '../util/modals/ModalCrearNotaFactura';
import ModalPDFBase64 from '../util/ModalPDFBase64';
import { MdWarning } from 'react-icons/md';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { FaFileInvoice, FaMinus, FaPlus, FaSearch } from 'react-icons/fa';
import { validarRangoFechas } from '../../miscelaneo/utilidadesFechas';
import { obtenerBadgeCodigoModificacion } from '../../miscelaneo/codigosModificacionDGII';

/**
 * Pagina principal para gestionar Notas de Credito/Debito de Facturas
 * Permite ver el listado global, crear nuevas notas y ver detalles
 */
function ProcesoNotasFactura() {
  const { usuario } = useContextoSesion();
  const { formatoMoneda, formatearFechaInput, formatearFechaHoraInput } = FuncionesGenerales();
  const { obtenerCodigosModificacion } = FuncionesProcesoNotasFactura();

  // Estados principales
  const [cargando, setCargando] = useState(false);
  const [modo, setModo] = useState('listado'); // 'listado', 'ver', 'crear'
  const [notaSeleccionada, setNotaSeleccionada] = useState(null);
  const [detallesNota, setDetallesNota] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);

  // Modales
  const [mostrarModalNotas, setMostrarModalNotas] = useState(false);
  const [mostrarModalFacturas, setMostrarModalFacturas] = useState(false);
  const [mostrarModalCrearNota, setMostrarModalCrearNota] = useState(false);
  const [tipoNotaCrear, setTipoNotaCrear] = useState('credito');
  const [facturaParaNota, setFacturaParaNota] = useState(null);

  // Filtro de tipo de nota para el listado
  const [filtroTipoNota, setFiltroTipoNota] = useState('todos');

  // Estados para PDF
  const [mostrarPDFNota, setMostrarPDFNota] = useState(false);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [cargandoPDF, setCargandoPDF] = useState(false);

  // Filtros de fecha (valores en inputs)
  const [fechaDesdeInput, setFechaDesdeInput] = useState('');
  const [fechaHastaInput, setFechaHastaInput] = useState('');
  const [clienteInput, setClienteInput] = useState('');

  // Filtros aplicados (valores que se envían al backend)
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [clienteBuscado, setClienteBuscado] = useState('');

  const [errorFechas, setErrorFechas] = useState('');

  // Codigos de modificacion
  const codigosModificacion = obtenerCodigosModificacion();

  // Validar rango de fechas mientras el usuario escribe (sin hacer request)
  useEffect(() => {
    if (fechaDesdeInput && fechaHastaInput) {
      const { valido, mensaje } = validarRangoFechas(fechaDesdeInput, fechaHastaInput);
      setErrorFechas(valido ? '' : mensaje);
    } else {
      setErrorFechas('');
    }
  }, [fechaDesdeInput, fechaHastaInput]);

  /**
   * Seleccionar una nota del listado
   */
  async function handleSeleccionarNota(nota) {
    try {
      setCargando(true);
      setMostrarModalNotas(false);

      const endpoint = nota.tipo_nota === 'credito'
        ? `/nota_credito_factura/${nota.id || nota.id_nota_credito_factura}`
        : `/nota_debito_factura/${nota.id || nota.id_nota_debito_factura}`;

      const respuesta = await instanciaAxios.get(endpoint);
      
      if (respuesta.status === 200) {
        setNotaSeleccionada({
          ...respuesta.data,
          tipo_nota: nota.tipo_nota
        });
        setDetallesNota(respuesta.data.detalles || []);
        setModo('ver');
      }
    } catch (error) {
      console.error('Error al cargar nota:', error);
      toast.error('Error al cargar los detalles de la nota');
    } finally {
      setCargando(false);
    }
  }

  /**
   * Seleccionar factura para crear nota
   * Carga los detalles de la factura antes de abrir el modal
   * @param {number} id_factura - ID de la factura seleccionada
   */
  async function handleSeleccionarFacturaParaNota(id_factura) {
    try {
      setCargando(true);
      setMostrarModalFacturas(false);
      
      // Cargar factura completa con detalles
      const respuesta = await instanciaAxios.get(`/factura/${id_factura}`);
      
      if (respuesta.status === 200) {
        const facturaCompleta = respuesta.data;
        
        // Preparar factura con detalles para el modal
        setFacturaParaNota({
          id_factura: facturaCompleta.id_factura,
          ncf: facturaCompleta.ncf,
          nombre_cliente: facturaCompleta.nombre_cliente,
          monto_total: facturaCompleta.monto_total,
          balance: parseFloat(facturaCompleta.balance) || 0, // Balance disponible para NC
          detalles_factura: (facturaCompleta.detalles_factura || []).map(d => ({
            id_detalle_factura: d.id_detalle_factura || d.id_producto,
            id_producto: d.id_producto,
            descripcion: d.descripcion || d.descripcion_producto,
            cantidad: parseFloat(d.cantidad) || 1,
            precio: parseFloat(d.precio) || 0,
            descuento: parseFloat(d.descuento) || 0,
            // Fix: usar check explícito para permitir itbis_tasa=0 (productos exentos)
            itbis_tasa: d.itbis_tasa !== undefined && d.itbis_tasa !== null 
              ? parseInt(d.itbis_tasa) 
              : 18,
            indicador_facturacion: d.indicador_facturacion || 1,
            id_tipo_producto: d.id_tipo_producto || 2,
            total: parseFloat(d.total) || (parseFloat(d.cantidad) * parseFloat(d.precio))
          }))
        });
        
        setMostrarModalCrearNota(true);
      }
    } catch (error) {
      console.error('Error al cargar factura:', error);
      toast.error('Error al cargar los detalles de la factura');
    } finally {
      setCargando(false);
    }
  }

  /**
   * Iniciar creacion de nota de credito
   */
  function handleCrearNotaCredito() {
    setTipoNotaCrear('credito');
    setMostrarModalFacturas(true);
  }

  /**
   * Iniciar creacion de nota de debito
   */
  function handleCrearNotaDebito() {
    setTipoNotaCrear('debito');
    setMostrarModalFacturas(true);
  }

  /**
   * Callback cuando se crea una nota exitosamente
   */
  function handleNotaCreada(nuevaNota) {
    setMostrarModalCrearNota(false);
    setFacturaParaNota(null);
    toast.success('Nota creada exitosamente');
    // Actualizar el listado si es necesario
    setModo('listado');
  }

  /**
   * Limpiar seleccion y volver al listado
   */
  function handleVolver() {
    setNotaSeleccionada(null);
    setDetallesNota([]);
    setModo('listado');
  }

  /**
   * Aplicar filtros de búsqueda
   * Solo dispara request cuando el usuario hace click en "Buscar"
   */
  function handleAplicarFiltros() {
    // Validar fechas antes de aplicar
    if (fechaDesdeInput && fechaHastaInput) {
      const { valido } = validarRangoFechas(fechaDesdeInput, fechaHastaInput);
      if (!valido) {
        toast.error('El rango de fechas no es válido');
        return;
      }
    }
    
    // Aplicar filtros (esto disparará el useEffect en TablaNotasFactura)
    setFechaDesde(fechaDesdeInput);
    setFechaHasta(fechaHastaInput);
    setClienteBuscado(clienteInput.trim());
  }

  /**
   * Limpiar todos los filtros
   */
  function handleLimpiarFiltros() {
    setFechaDesdeInput('');
    setFechaHastaInput('');
    setClienteInput('');
    setFechaDesde('');
    setFechaHasta('');
    setClienteBuscado('');
    setErrorFechas('');
  }

  /**
   * Ver PDF de la nota seleccionada
   */
  async function handleVerPDF() {
    if (!notaSeleccionada) {
      toast.error('Seleccione una nota primero');
      return;
    }

    if (!notaSeleccionada.dgii_track_id) {
      toast.error('Esta nota no tiene comprobante enviado a DGII');
      return;
    }

    try {
      setCargandoPDF(true);
      
      const endpoint = notaSeleccionada.tipo_nota === 'credito'
        ? `/nota_credito_factura/${notaSeleccionada.id_nota_credito_factura}/pdf`
        : `/nota_debito_factura/${notaSeleccionada.id_nota_debito_factura}/pdf`;

      const respuesta = await instanciaAxios.get(endpoint);

      if (respuesta.data?.data?.pdf_base64) {
        setPdfBase64(respuesta.data.data.pdf_base64);
        setMostrarPDFNota(true);
      } else {
        toast.error('No se pudo obtener el PDF');
      }
    } catch (error) {
      console.error('Error al obtener PDF:', error);
      toast.error('Error al obtener el PDF de la nota');
    } finally {
      setCargandoPDF(false);
    }
  }

  // Obtener descripcion del codigo de modificacion
  const getDescripcionCodigo = (codigo) => {
    const encontrado = codigosModificacion.find(c => c.codigo === parseInt(codigo));
    return encontrado ? encontrado.descripcion : `Codigo ${codigo}`;
  };

  /**
   * Renderiza badge con tipo de modificación de la nota
   * Reutiliza utility compartida para consistencia
   */
  const renderTipoModificacion = useCallback((codigoModificacion) => {
    const codigo = parseInt(codigoModificacion);
    const descripcion = getDescripcionCodigo(codigo);
    const badgeClass = obtenerBadgeCodigoModificacion(codigo);
    
    return (
      <span 
        className={`px-3 py-1 rounded-full text-xs font-medium border ${badgeClass}`}
        role="status"
        aria-label={`Tipo de modificación: ${descripcion}`}
      >
        {descripcion}
      </span>
    );
  }, []);

  return (
    cargando ? (
      <SpinnerCargando />
    ) : (
      <div className="min-h-screen bg-gray-200 font-sans">
        <div className="flex justify-center py-10">
          <div className="bg-gray-100 bg-opacity-90 rounded-xl border p-8 w-full max-w-7xl mx-10">
            
            {/* Header */}
            <div className="flex justify-between items-start mb-8">
              <div className="space-y-1">
                <h1 className="font-cascadia font-bold text-2xl">
                  Notas de Credito/Debito
                </h1>
                <p className="text-gray-500">
                  Gestione las notas de credito (E34) y debito (E33) de facturas de venta.
                </p>
              </div>

              {/* Botones de accion rapida */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleCrearNotaCredito}
                  className="flex items-center gap-2 px-4 py-2 bg-color-secundario text-white rounded-xl hover:bg-opacity-90 transition-colors"
                >
                  <FaMinus size={14} />
                  Nueva NC
                </button>
                <button
                  type="button"
                  onClick={handleCrearNotaDebito}
                  className="flex items-center gap-2 px-4 py-2 bg-color-primario text-white rounded-xl hover:bg-opacity-90 transition-colors"
                >
                  <FaPlus size={14} />
                  Nueva ND
                </button>
              </div>
            </div>

            {/* Filtros de tipo */}
            <div className="flex gap-2 mb-6">
              <button
                type="button"
                onClick={() => setFiltroTipoNota('todos')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  filtroTipoNota === 'todos'
                    ? 'bg-color-secundario text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                }`}
              >
                Todas
              </button>
              <button
                type="button"
                onClick={() => setFiltroTipoNota('credito')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  filtroTipoNota === 'credito'
                    ? 'bg-color-secundario text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                }`}
              >
                Notas de Crédito (E34)
              </button>
              <button
                type="button"
                onClick={() => setFiltroTipoNota('debito')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  filtroTipoNota === 'debito'
                    ? 'bg-color-primario text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                }`}
              >
                Notas de Débito (E33)
              </button>
            </div>

            {/* Filtros de búsqueda */}
            <div className="bg-white rounded-xl border p-4 mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Filtros de Búsqueda</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* Filtro: Fecha Desde */}
                <div>
                  <label 
                    htmlFor="fecha-desde"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Fecha Desde
                  </label>
                  <input
                    id="fecha-desde"
                    type="date"
                    value={fechaDesdeInput}
                    onChange={(e) => setFechaDesdeInput(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-color-primario focus:border-transparent"
                    aria-label="Filtrar notas desde esta fecha"
                  />
                </div>
                
                {/* Filtro: Fecha Hasta */}
                <div>
                  <label 
                    htmlFor="fecha-hasta"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Fecha Hasta
                  </label>
                  <input
                    id="fecha-hasta"
                    type="date"
                    value={fechaHastaInput}
                    onChange={(e) => setFechaHastaInput(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-color-primario focus:border-transparent"
                    aria-label="Filtrar notas hasta esta fecha"
                  />
                </div>
                
                {/* Filtro: Cliente */}
                <div>
                  <label 
                    htmlFor="cliente-busqueda"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Cliente
                  </label>
                  <input
                    id="cliente-busqueda"
                    type="text"
                    value={clienteInput}
                    onChange={(e) => setClienteInput(e.target.value)}
                    placeholder="Buscar por nombre..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-color-primario focus:border-transparent"
                    aria-label="Filtrar notas por nombre de cliente"
                  />
                </div>
              </div>
              
              {/* Mensaje de error de validación */}
              {errorFechas && (
                <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 rounded-md">
                  <p className="text-sm text-red-700">{errorFechas}</p>
                </div>
              )}
              
              {/* Botones de acción */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleAplicarFiltros}
                  className="flex items-center gap-2 px-4 py-2 bg-color-primario text-white rounded-lg hover:bg-opacity-90 transition-colors text-sm font-medium"
                  aria-label="Aplicar filtros de búsqueda"
                >
                  <FaSearch size={14} />
                  Buscar
                </button>
                <button
                  type="button"
                  onClick={handleLimpiarFiltros}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Limpiar filtros"
                >
                  Limpiar filtros
                </button>
              </div>
            </div>

            {/* Contenido principal */}
            {modo === 'listado' && (
              <div className="bg-white rounded-xl border p-4">
                <TablaNotasFactura
                  tipoNota={filtroTipoNota}
                  onSeleccionar={handleSeleccionarNota}
                  fechaDesde={fechaDesde}
                  fechaHasta={fechaHasta}
                  clienteBuscado={clienteBuscado}
                />
              </div>
            )}

            {modo === 'ver' && notaSeleccionada && (
              <div className="space-y-6">
                {/* Boton volver */}
                <button
                  type="button"
                  onClick={handleVolver}
                  className="text-color-primario hover:underline text-sm"
                >
                  ← Volver al listado
                </button>

                {/* Alertas de estado */}
                {notaSeleccionada.dgii_estado?.toLowerCase() === 'rechazado' && (
                  <div className="flex items-center gap-2 p-3 bg-red-100 border-l-4 border-red-500 rounded-md">
                    <MdWarning className="text-red-500 text-xl" />
                    <p className="text-red-700 font-medium">
                      Esta nota fue rechazada por DGII. Revise los mensajes de error.
                    </p>
                  </div>
                )}

                {notaSeleccionada.anulado && (
                  <div className="flex items-center gap-2 p-3 bg-gray-100 border-l-4 border-gray-500 rounded-md">
                    <MdWarning className="text-gray-500 text-xl" />
                    <p className="text-gray-700 font-medium">
                      Esta nota ha sido anulada.
                    </p>
                  </div>
                )}

                {/* Info de la nota */}
                <div className="bg-white rounded-xl border p-6">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="text-xl font-semibold">
                        {notaSeleccionada.tipo_nota === 'credito' ? 'Nota de Credito (E34)' : 'Nota de Debito (E33)'}
                      </h2>
                      <p className="font-mono text-lg mt-1">{notaSeleccionada.ncf}</p>
                    </div>
                    {renderTipoModificacion(notaSeleccionada.codigo_modificacion)}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">NCF Modificado</label>
                      <p className="font-mono text-sm">{notaSeleccionada.ncf_modificado || '—'}</p>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Cliente</label>
                      <p className="text-sm">{notaSeleccionada.nombre_cliente || '—'}</p>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Fecha</label>
                      <p className="text-sm">{formatearFechaHoraInput(notaSeleccionada.fecha_hora_creacion) || '—'}</p>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Razon Modificacion</label>
                      <p className="text-sm">{getDescripcionCodigo(notaSeleccionada.codigo_modificacion)}</p>
                    </div>
                  </div>

                  {notaSeleccionada.razon_modificacion && (
                    <div className="mt-4">
                      <label className="block text-xs text-gray-500 mb-1">Descripcion Adicional</label>
                      <p className="text-sm">{notaSeleccionada.razon_modificacion}</p>
                    </div>
                  )}

                  {/* Botón Ver PDF */}
                  {notaSeleccionada.dgii_track_id && (
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={handleVerPDF}
                        disabled={cargandoPDF}
                        className="flex items-center gap-2 px-4 py-2 bg-color-primario text-white rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50"
                      >
                        <FaFileInvoice size={16} />
                        {cargandoPDF ? 'Generando PDF...' : 'Ver PDF'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Detalles */}
                <div className="bg-white rounded-xl border p-6">
                  <h3 className="font-semibold mb-4">Detalles de la Nota</h3>
                  {detallesNota.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-color-primario text-white">
                          <tr>
                            <th className="text-left px-3 py-2">Descripción</th>
                            <th className="text-center px-3 py-2">Cantidad</th>
                            <th className="text-right px-3 py-2">Precio</th>
                            <th className="text-center px-3 py-2">ITBIS</th>
                            <th className="text-right px-3 py-2">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detallesNota.map((detalle, index) => (
                            <tr key={index} className="border-t">
                              <td className="px-3 py-2">{detalle.descripcion_producto || detalle.descripcion}</td>
                              <td className="text-center px-3 py-2">{detalle.cantidad}</td>
                              <td className="text-right px-3 py-2">{formatoMoneda(detalle.precio)}</td>
                              <td className="text-center px-3 py-2">{detalle.itbis_tasa}%</td>
                              <td className="text-right px-3 py-2 font-medium">{formatoMoneda(detalle.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-400 text-center py-4">No hay detalles disponibles</p>
                  )}
                </div>

                {/* Totales */}
                <div className="bg-white rounded-xl border p-6">
                  <div className="flex justify-end">
                    <div className="w-72 space-y-2">
                      <div className="flex justify-between text-gray-600">
                        <span>Subtotal:</span>
                        <span className="font-medium">{formatoMoneda(notaSeleccionada.monto)}</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>ITBIS:</span>
                        <span className="font-medium">{formatoMoneda(notaSeleccionada.impuesto_total)}</span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span className="font-semibold">Total:</span>
                        <span className="font-bold text-xl">{formatoMoneda(notaSeleccionada.monto_total)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Comentario */}
                {notaSeleccionada.comentario && (
                  <div className="bg-white rounded-xl border p-6">
                    <h3 className="font-semibold mb-2">Comentario Interno</h3>
                    <p className="text-gray-600">{notaSeleccionada.comentario}</p>
                  </div>
                )}

                {/* Info DGII */}
                {notaSeleccionada.dgii_track_id && (
                  <div className="bg-white rounded-xl border p-6">
                    <h3 className="font-semibold mb-4">Informacion DGII</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <label className="block text-xs text-gray-500">Track ID</label>
                        <p className="font-mono">{notaSeleccionada.dgii_track_id}</p>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">Fecha Recepcion</label>
                        <p>{notaSeleccionada.dgii_fecha_recepcion || '—'}</p>
                      </div>
                    </div>
                    {notaSeleccionada.dgii_mensaje && (
                      <div className="mt-4">
                        <label className="block text-xs text-gray-500">Mensaje DGII</label>
                        <p className="text-sm p-2 bg-gray-50 rounded">{notaSeleccionada.dgii_mensaje}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>

        {/* Modal buscar notas */}
        <PlantillaModal 
          mostrar={mostrarModalNotas} 
          onClose={() => setMostrarModalNotas(false)}
          titulo="Buscar Nota"
        >
          <TablaNotasFactura 
            tipoNota={filtroTipoNota}
            onSeleccionar={handleSeleccionarNota}
            fechaDesde={fechaDesde}
            fechaHasta={fechaHasta}
            clienteBuscado={clienteBuscado}
          />
        </PlantillaModal>

        {/* Modal seleccionar factura para crear nota */}
        <PlantillaModal 
          mostrar={mostrarModalFacturas} 
          onClose={() => setMostrarModalFacturas(false)}
          titulo={`Seleccionar factura para Nota de ${tipoNotaCrear === 'credito' ? 'Credito' : 'Debito'}`}
        >
          <TablaFactura 
            setFacturaSeleccionada={handleSeleccionarFacturaParaNota}
            onClose={() => setMostrarModalFacturas(false)}
          />
        </PlantillaModal>

        {/* Modal crear nota */}
        <ModalCrearNotaFactura
          mostrar={mostrarModalCrearNota}
          onClose={() => {
            setMostrarModalCrearNota(false);
            setFacturaParaNota(null);
          }}
          facturaOriginal={facturaParaNota}
          tipoNota={tipoNotaCrear}
          onExito={handleNotaCreada}
        />

        {/* Modal: PDF */}
        <ModalPDFBase64
          mostrar={mostrarPDFNota}
          onClose={() => {
            setMostrarPDFNota(false);
            setPdfBase64(null);
          }}
          pdfBase64Directo={pdfBase64}
          ncf={notaSeleccionada?.ncf}
          tituloDocumento={
            notaSeleccionada?.tipo_nota === 'credito'
              ? 'Nota de Crédito E34'
              : 'Nota de Débito E33'
          }
          tipoDocumento="nota_credito"
        />
      </div>
    )
  );
}

export default ProcesoNotasFactura;

